import type posthog from 'posthog-js';
import {
  resolveCleanupFeedbackVariant,
  type CleanupFeedbackVariant,
} from './cleanup/cleanup-feedback-types';
import { Logger } from './logger';

type PostHog = typeof posthog;

interface CleanupFeedbackPostHogDependencies {
  allowed: () => boolean
  getInstance: () => PostHog | null
  load: () => Promise<void>
  trimString: (value: unknown) => string | undefined
}

export type CleanupFeedbackSurveyState = 'disabled' | 'loading' | 'ready' | 'unavailable';

const SURVEY_ID_PLACEHOLDER_PATTERNS = [
  /^your-.*survey.*$/i,
  /^survey-id-123$/i,
];

/**
 * PostHog-side helpers for the Cleanup feedback action.
 *
 * Since #707 PostHog no longer renders, hosts, or receives the feedback form -
 * that is a first-party `POST /v1/feedback` call. What remains here is the
 * consent-gated experiment cohort for the 45s timed nudge (an analytics
 * experiment, so it correctly degrades to `manual_only` without consent) and
 * the configured survey id, kept only for the optional mirror.
 */
export function createCleanupFeedbackPostHog({
  allowed,
  getInstance,
  load,
  trimString,
}: CleanupFeedbackPostHogDependencies) {
  const resolveSurveyId = (): string | undefined => {
    const trimmed = trimString(process.env.NEXT_PUBLIC_POSTHOG_CLEANUP_FEEDBACK_SURVEY_ID);
    if (!trimmed || SURVEY_ID_PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(trimmed))) {
      return undefined;
    }
    return trimmed;
  };

  const getExperimentVariant = (): CleanupFeedbackVariant => {
    const flagKey = trimString(process.env.NEXT_PUBLIC_POSTHOG_CLEANUP_FEEDBACK_FLAG_KEY);
    const instance = getInstance();
    if (!flagKey || !allowed() || !instance) {
      return 'manual_only';
    }
    try {
      return resolveCleanupFeedbackVariant(instance.getFeatureFlag(flagKey));
    } catch {
      return 'manual_only';
    }
  };

  const subscribeExperimentVariant = (
    callback: (variant: CleanupFeedbackVariant) => void,
  ): (() => void) => {
    let cancelled = false;
    let unsubscribe = () => {};
    void load().then(() => {
      const flagKey = trimString(process.env.NEXT_PUBLIC_POSTHOG_CLEANUP_FEEDBACK_FLAG_KEY);
      const instance = getInstance();
      if (cancelled) {
        return;
      }
      if (!flagKey || !allowed() || !instance) {
        callback('manual_only');
        return;
      }
      unsubscribe = instance.onFeatureFlags(() => {
        if (!cancelled) {
          callback(resolveCleanupFeedbackVariant(instance.getFeatureFlag(flagKey)));
        }
      });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  };

  const logDiagnostic = (
    message: string,
    detail?: Record<string, string | number | boolean>,
  ): void => {
    Logger.warn(`[cleanup-feedback] ${message}`, detail);
  };

  return {
    getExperimentVariant,
    logDiagnostic,
    resolveSurveyId,
    subscribeExperimentVariant,
  };
}
